import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { 
  faSort, 
  faSortDown, 
  faSortUp,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faCircle,
  faEye,
  faTrash,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { IAnamneseDetail } from 'src/app/models/anamnese.model';
import { AnamneseService } from 'src/app/services/anamnese.service';
import { ModulesService } from 'src/app/services/modules.service';
import { SearchService } from '../../services/search.service';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';

@Component({
  selector: 'app-pending-anamneses-v2',
  templateUrl: './pending-anamneses.component.html',
  styleUrls: ['./pending-anamneses.component.sass']
})
export class PendingAnamnesesV2Component implements OnInit, OnDestroy {
  Math = Math;
  loading = false;
  pageSize = 10;
  currentPage = 0;
  totalPages = 1;
  totalElements = 0;
  sortBy = 'DESC';
  orderBy = 'createdAt';
  
  anamneses: IAnamneseDetail[] = [];
  filterType: 'COMPLETED' | 'NOT_COMPLETED' | 'ALL' = 'NOT_COMPLETED';
  initialDate = '';
  finalDate = '';

  private searchSubscription?: Subscription;

  // Icons
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faAngleDoubleLeft = faAngleDoubleLeft;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faAngleDoubleRight = faAngleDoubleRight;
  faCircle = faCircle;
  faEye = faEye;
  faTrash = faTrash;
  faTimes = faTimes;

  // Delete
  showConfirmDeletePopup = false;
  confirmDeleteLoading = false;
  pendingDeleteAnamneseId: string | null = null;

  pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];

  viewModules$: Observable<{ moduleName: string; moduleNamePascalCase: string; moduleNameInSingular: string; }>;

  constructor(
    private anamneseService: AnamneseService,
    private router: Router,
    private searchService: SearchService,
    public modulesService: ModulesService,
    private store: Store
  ) {
    this.viewModules$ = combineLatest({
      moduleName: this.modulesService.moduleName$,
      moduleNamePascalCase: this.modulesService.moduleNamePascalCase$,
      moduleNameInSingular: this.modulesService.moduleNameInSingular$
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.searchService.setSearchTerm('');
    this.getAnamneses(0, this.pageSize);

    this.searchSubscription = this.searchService.searchTerm$.subscribe((term: string) => {
      // Busca não implementada ainda no backend para anamneses
      // Por enquanto só recarrega
      this.getAnamneses(0, this.pageSize);
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  getAnamneses(page: number, size: number): void {
    this.loading = true;
    
    this.anamneseService.listAnamnesesWithFilters(
      page,
      size,
      this.sortBy,
      this.orderBy,
      '', // patientId vazio para listar todas
      this.filterType,
      this.initialDate,
      this.finalDate,
      this.modulesService.moduleId || ''
    ).subscribe({
      next: (response) => {
        this.anamneses = response.data;
        this.currentPage = response.page_info.current_page;
        this.totalPages = response.page_info.total_pages;
        this.totalElements = response.page_info.total_elements;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao buscar anamneses:', error);
        this.loading = false;
      }
    });
  }

  toggleSort(column: string): void {
    if (this.orderBy === column) {
      this.sortBy = this.sortBy === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.orderBy = column;
      this.sortBy = 'ASC';
    }
    this.getAnamneses(this.currentPage, this.pageSize);
  }

  changeFilter(filter: 'COMPLETED' | 'NOT_COMPLETED' | 'ALL'): void {
    this.filterType = filter;
    this.getAnamneses(0, this.pageSize);
  }

  onDateChange(): void {
    this.getAnamneses(0, this.pageSize);
  }

  clearDateFilters(): void {
    this.initialDate = '';
    this.finalDate = '';
    this.getAnamneses(0, this.pageSize);
  }

  /**
   * Gera a base da rota para navegação baseada no módulo atual
   */
  private getModuleRouteBase(): string[] {
    const moduleName = (this.modulesService.moduleName || '').toString().toUpperCase();
    
    // Módulo PACIENTES usa rota fixa
    if (moduleName === 'PACIENTES') {
      return ['main', 'v2', 'patients'];
    }
    
    // Outros módulos usam a rota dinâmica
    return ['main', 'v2', 'modules', moduleName.toLowerCase()];
  }

  viewAnamnese(anamnese: IAnamneseDetail): void {
    const routeSuffix = anamnese.is_answered ? 'answers' : 'view';
    const base = this.getModuleRouteBase();

    // Sempre navegar para o fluxo de anamnese dentro do layout novo
    this.router.navigate([...base, 'anamnese', anamnese.id || '', routeSuffix], {
      queryParams: anamnese.patient_id ? { patientId: anamnese.patient_id } : {}
    });
  }

  goToPatientList(): void {
    const base = this.getModuleRouteBase();
    this.router.navigate(base, { queryParams: { headerSelectedOption: 'Anamnese' } });
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  getStatusClass(isAnswered: boolean | undefined): string {
    return isAnswered ? 'text-emerald-500' : 'text-orange-500';
  }

  getStatusText(isAnswered: boolean | undefined): string {
    return isAnswered ? 'Respondida' : 'Pendente';
  }

  // Paginação
  changePageSize(event: Event): void {
    this.pageSize = +(<HTMLSelectElement>event.target).value;
    this.getAnamneses(0, this.pageSize);
  }

  goToFirstPage(): void {
    if (this.currentPage > 0) {
      this.getAnamneses(0, this.pageSize);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.getAnamneses(this.currentPage - 1, this.pageSize);
    }
  }

  goToNextPage(): void {
    if (this.currentPage + 1 < this.totalPages) {
      this.getAnamneses(this.currentPage + 1, this.pageSize);
    }
  }

  goToLastPage(): void {
    if (this.currentPage + 1 < this.totalPages) {
      this.getAnamneses(this.totalPages - 1, this.pageSize);
    }
  }

  generatePagination(current: number, total: number): (string | number)[] {
    const range = (start: number, end: number): number[] => {
      return Array.from({ length: (end - start) }, (v, k) => k + start);
    };
    let pages: (string | number)[] = [1];
    let left = current - 1;
    let right = current + 1;
    if (left < 2) right += 2 - left;
    if (right > total - 1) left -= right - (total - 1);
    left = Math.max(2, left);
    right = Math.min(total - 1, right);
    if (left > 2) pages.push('...');
    pages = pages.concat(range(left, right + 1));
    if (right < total - 1) pages.push('...');
    if (total > 1 && total !== pages[pages.length - 1]) pages.push(total);
    return pages;
  }

  navigateTo(page: number | string): void {
    if (page !== '...') {
      this.getAnamneses(+page - 1, this.pageSize);
    }
  }

  // ========== Delete Anamnese Feature ==========

  deleteAnamnese(anamneseId: string): void {
    if (!anamneseId) return;
    this.pendingDeleteAnamneseId = anamneseId;
    this.showConfirmDeletePopup = true;
  }

  closeDeleteModal(): void {
    this.showConfirmDeletePopup = false;
    this.pendingDeleteAnamneseId = null;
  }

  confirmDelete(): void {
    const id = this.pendingDeleteAnamneseId;
    if (!id) return;
    this.confirmDeleteLoading = true;
    this.anamneseService.deleteAnamnese(id).subscribe({
      next: () => {
        this.store.dispatch(setWarningText('Anamnese excluída com sucesso.', 200));
        this.closeDeleteModal();
        this.getAnamneses(this.currentPage, this.pageSize);
        this.confirmDeleteLoading = false;
      },
      error: (error: any) => {
        console.error('Erro ao excluir anamnese:', error);
        const errMsg = error?.error?.message || 'Erro ao excluir anamnese. Tente novamente.';
        const errStatus = error?.status || 500;
        this.store.dispatch(setWarningText(errMsg, errStatus));
        this.closeDeleteModal();
        this.getAnamneses(this.currentPage, this.pageSize);
        this.confirmDeleteLoading = false;
      }
    });
  }
}
